import { createJSONStorage, persist } from 'zustand/middleware'

import { create } from 'zustand'

import apiClient from '@/api/axios'

import { decryptData, encryptData } from '@/lib/crypto'
import { logger } from '@/lib/logger'

import { API_ENDPOINTS } from '../api/endpoints'
import { BranchSwitcher } from '../features/branches/types/branch.types'

type BranchState = {
  branches: BranchSwitcher[]
  activeBranch: BranchSwitcher | null
  setBranches: (branches: BranchSwitcher[]) => void
  setActiveBranch: (branch: BranchSwitcher) => void
  fetchBranches: () => Promise<void>
}

const PSEUDO_BRANCH: BranchSwitcher = {
  id: 'all',
  name: 'All Branches',
  city: '',
  is_active: true,
}

export const useBranchStore = create<BranchState>()(
  persist(
    (set, get) => ({
      branches: [],
      activeBranch: null,

      setBranches: (branches) => {
        // Inject pseudo branch at the start
        const allBranches = [PSEUDO_BRANCH, ...branches]

        const { activeBranch } = get()
        const stillExists = activeBranch ? allBranches.find((b) => b.id === activeBranch.id) : null

        set({
          branches: allBranches,
          activeBranch: stillExists || allBranches[0], // default to "All Branches"
        })
      },

      setActiveBranch: (branch) => {
        set({ activeBranch: branch })
      },

      fetchBranches: async () => {
        try {
          const response = await apiClient.get(API_ENDPOINTS.branches.switcher)
          const branches: BranchSwitcher[] = response?.data?.data.data ?? []
          get().setBranches(branches)
        } catch (error) {
          logger.error('Failed to fetch branches:', error)
          set({ branches: [PSEUDO_BRANCH], activeBranch: PSEUDO_BRANCH })
        }
      },
    }),
    {
      name: 'bbg-branch',
      storage: createJSONStorage(() => ({
        getItem: (name) => {
          const encrypted = localStorage.getItem(name)
          if (!encrypted) return null
          try {
            const decrypted = decryptData(encrypted)
            return decrypted
          } catch (error) {
            // Silently remove corrupted data
            localStorage.removeItem(name)
            return null
          }
        },
        setItem: (name, value) => {
          try {
            const encrypted = encryptData(value)
            localStorage.setItem(name, encrypted)
          } catch (error) {
            logger.error('Failed to encrypt branch data', error)
            localStorage.setItem(name, value)
          }
        },
        removeItem: (name) => localStorage.removeItem(name),
      })),
      partialize: (state) => ({
        activeBranch: state.activeBranch,
      }),
    }
  )
)
